import React from "react";
import PropTypes from "prop-types";
import { Select } from "antd";
import styled from "styled-components";

import {
  multiSelectChipAndFocusCss,
  buildMultiSelectDropdownGlobalStyle
} from "@tailormed/common-client/component/Select/MultiSelectFieldStyles";

const { Option } = Select;

const FACILITIES_DROPDOWN_CLASS = "tm-facilities-dropdown";

const StyledSelect = styled(Select)`
  ${multiSelectChipAndFocusCss}
`;

const FacilitiesDropdownGlobalStyle = buildMultiSelectDropdownGlobalStyle(FACILITIES_DROPDOWN_CLASS);

const FacilitiesSelectField = ({
  facilities,
  value,
  onChange,
  loading,
  disabled,
  placeholder,
  bordered = true,
  showArrow,
  allowClear = false,
  maxTagCount = 1,
  maxTagTextLength,
  maxTagPlaceholder,
  dropdownMatchSelectWidth = false,
  dropdownStyle,
  style,
  autoFocus,
  defaultOpen,
  open,
  onBlur,
  onKeyDown,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  onDropdownVisibleChange,
  getPopupContainer = (trigger) => trigger.parentNode,
  id
}) => {
  const valueArray = Array.isArray(value) ? value : [];

  const compareFacilities = (a, b) => {
    const aSelected = valueArray.indexOf(a.value) > -1;
    const bSelected = valueArray.indexOf(b.value) > -1;
    if (aSelected === bSelected) {
      return (a.children || "").localeCompare(b.children || "");
    }
    return aSelected ? -1 : 1;
  };

  const filterOption = (input, option) => option.children?.toLowerCase()?.includes(input?.toLowerCase());

  const handleChange = (next) => {
    if (typeof onChange === "function") onChange(Array.isArray(next) ? next : []);
  };

  return (
    <div id={id} style={{ width: "100%" }}>
      <FacilitiesDropdownGlobalStyle />
      <StyledSelect
        mode="multiple"
        value={valueArray}
        onChange={handleChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDropdownVisibleChange={onDropdownVisibleChange}
        loading={loading}
        disabled={disabled}
        placeholder={placeholder}
        bordered={bordered}
        showArrow={showArrow}
        allowClear={allowClear}
        maxTagCount={maxTagCount}
        maxTagTextLength={maxTagTextLength}
        maxTagPlaceholder={maxTagPlaceholder}
        dropdownMatchSelectWidth={dropdownMatchSelectWidth}
        dropdownClassName={FACILITIES_DROPDOWN_CLASS}
        dropdownStyle={dropdownStyle}
        virtual={false}
        style={style}
        autoFocus={autoFocus}
        defaultOpen={defaultOpen}
        open={open}
        filterOption={filterOption}
        filterSort={compareFacilities}
        getPopupContainer={getPopupContainer}
      >
        {(facilities || []).map((facility, index) => (
          <Option key={index} value={facility.id} disabled={loading}>
            {facility.name}
          </Option>
        ))}
      </StyledSelect>
    </div>
  );
};

FacilitiesSelectField.propTypes = {
  facilities: PropTypes.array.isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.node,
  bordered: PropTypes.bool,
  showArrow: PropTypes.bool,
  allowClear: PropTypes.bool,
  maxTagCount: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(["responsive"])]),
  maxTagTextLength: PropTypes.number,
  maxTagPlaceholder: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  dropdownMatchSelectWidth: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  dropdownStyle: PropTypes.object,
  style: PropTypes.object,
  autoFocus: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  open: PropTypes.bool,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  onFocus: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onDropdownVisibleChange: PropTypes.func,
  getPopupContainer: PropTypes.func,
  id: PropTypes.string
};

export default FacilitiesSelectField;
